import { useEffect, useState } from "react";
import { StatCard, AgingBar } from "@/components/DashboardWidgets";
import ConsolidatedRailCard from "@/components/ConsolidatedRailCard";
import api from "@/lib/api";
import { useCurrencyRail } from "@/context/CurrencyRailContext";
import { fm } from "@/components/payables/shared";

export function ApOverviewTab({ onOpen }) {
  const [data, setData] = useState(null);
  const { activeRail, filterRails } = useCurrencyRail();
  useEffect(() => {
    (async () => {
      try {
        const { data } = await api.get("/payables/summary");
        setData(data);
      } catch (err) {
        console.warn("[Payables] summary failed:", err?.message);
      }
    })();
  }, []);
  if (!data) return <div className="text-sm text-[var(--muted-text)]">Loading…</div>;
  const rails = filterRails(data.by_currency || []);
  const showMixed = activeRail === "ALL";
  return (
    <div className="space-y-6" data-testid="ap-overview">
      {showMixed && (
        <>
          <ConsolidatedRailCard
            rails={(data.by_currency || []).map((r) => ({
              currency: r.currency,
              outstanding: r.totals?.outstanding ?? 0,
              overdue: r.totals?.overdue ?? 0,
              paid_this_month: r.totals?.paid_this_month ?? 0,
              billed_ytd: r.totals?.billed_ytd ?? 0,
            }))}
            testId="ap-consolidated"
            subtitle="live FX across every AP rail"
            fields={[
              { key: "outstanding",     label: "Outstanding",     color: "text-[#2F4F4F]" },
              { key: "overdue",         label: "Overdue",         color: "text-[#B4283A]" },
              { key: "paid_this_month", label: "Paid this month", color: "text-[#4C956C]" },
              { key: "billed_ytd",      label: "Billed YTD",      color: "text-[#E07A5F]" },
            ]}
          />
          <div className="grid grid-cols-2 sm:grid-cols-4 gap-3">
            <StatCard label="Outstanding · native sum" value={data.totals.outstanding} currency=""
                      sublabel={`${data.counts.outstanding} bills · mixed currencies`} tone="brand"
                      testid="ap-stat-outstanding" />
            <StatCard label="Overdue · native sum" value={data.totals.overdue} currency=""
                      sublabel={`${data.counts.overdue} past due · mixed currencies`}
                      tone={data.totals.overdue > 0 ? "danger" : "default"}
                      testid="ap-stat-overdue" />
            <StatCard label="Paid this month · native sum" value={data.totals.paid_this_month} currency=""
                      sublabel="Mixed currencies · pick a rail to isolate"
                      testid="ap-stat-paid-mtd" />
            <StatCard label="Billed YTD · native sum" value={data.totals.billed_ytd} currency=""
                      sublabel={`${data.counts.total_bills} bills · mixed currencies`}
                      testid="ap-stat-billed-ytd" />
          </div>
        </>
      )}
      {rails.map((rail) => (
        <div key={`ap-ov-${rail.currency}`} data-testid={`ap-overview-rail-${rail.currency}`} className="space-y-3">
          <div className="flex items-center gap-2 text-[10px] tracking-[0.2em] uppercase text-[var(--muted-text)]">
            <span className="px-1.5 py-0.5 rounded font-mono text-[10px] text-[#FDFCF8]"
                  style={{ backgroundColor: rail.currency === "USD" ? "#1E5A96" : "#4C956C" }}>{rail.currency}</span>
            <span>rail</span>
          </div>
          <div className="grid grid-cols-2 sm:grid-cols-4 gap-3">
            <StatCard label="Outstanding" value={rail.totals.outstanding} currency={rail.currency}
                      sublabel={`${rail.counts.outstanding} bills`} tone="brand"
                      testid={`ap-stat-outstanding-${rail.currency}`} />
            <StatCard label="Overdue" value={rail.totals.overdue} currency={rail.currency}
                      sublabel={`${rail.counts.overdue} past due`}
                      tone={rail.totals.overdue > 0 ? "danger" : "default"}
                      testid={`ap-stat-overdue-${rail.currency}`} />
            <StatCard label="Paid this month" value={rail.totals.paid_this_month} currency={rail.currency}
                      sublabel="Cash out to vendors"
                      testid={`ap-stat-paid-mtd-${rail.currency}`} />
            <StatCard label="Billed YTD" value={rail.totals.billed_ytd} currency={rail.currency}
                      sublabel="This year"
                      testid={`ap-stat-billed-ytd-${rail.currency}`} />
          </div>
          <div className="grid grid-cols-1 lg:grid-cols-2 gap-4">
            <div className="card-flat p-5">
              <div className="flex items-center justify-between mb-3">
                <div className="text-xs tracking-[0.2em] uppercase text-[var(--muted-text)]">Aging</div>
                <button onClick={() => onOpen("aging")} className="text-xs text-[var(--brand)] hover:underline">View aged AP →</button>
              </div>
              <AgingBar buckets={rail.aging} currency={rail.currency} testidPrefix={`ap-aging-${rail.currency}`} />
            </div>
            <div className="card-flat p-5">
              <div className="flex items-center justify-between mb-3">
                <div className="text-xs tracking-[0.2em] uppercase text-[var(--muted-text)]">Top creditors</div>
                <button onClick={() => onOpen("bills")} className="text-xs text-[var(--brand)] hover:underline">View all bills →</button>
              </div>
              {rail.top_creditors.length === 0 ? (
                <div className="text-sm text-[var(--muted-text)] py-4">No open bills — you're clear!</div>
              ) : (
                <table className="w-full text-sm">
                  <tbody>
                    {rail.top_creditors.map((d) => (
                      <tr key={`${rail.currency}-${d.vendor}`} className="border-b border-[var(--divider)] last:border-none">
                        <td className="py-2 truncate">{d.vendor}</td>
                        <td className="py-2 text-right font-mono-data font-medium">{fm(d.balance, rail.currency)}</td>
                      </tr>
                    ))}
                  </tbody>
                </table>
              )}
            </div>
          </div>
        </div>
      ))}
    </div>
  );
}
